import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import paymentsService from '../services/paymentsService';
import s3Service from '../services/s3Service';
import { toast } from 'react-toastify';
import styles from '../assets/paymentEdit.module.css';

const PaymentEdit = ({ isInitialPayment = false }) => {
  const navigate = useNavigate();
  const { rentId, paymentId } = useParams();
  
  // Handle both route patterns: /rents/:rentId/payments/:paymentId/edit and /payments/:paymentId/edit
  // Also handle /rents/:rentId/payments/:paymentId/edit-initial for initial payments
  const effectivePaymentId = paymentId;
  const effectiveRentId = rentId || 'unknown';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    due_date: '',
    status: '',
    amount_paid: '',
    payment_date: '',
    bank_account_id: '',
    notes: ''
  });
  const [rent, setRent] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [property, setProperty] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payment, setPayment] = useState(null);
  const [guardianImageUrl, setGuardianImageUrl] = useState(null);

  // Refs for date picker inputs
  const dueDateInputRef = useRef(null);
  const paymentDateInputRef = useRef(null);

  useEffect(() => {
    loadEditData();
  }, [effectiveRentId, effectivePaymentId]);

  const loadEditData = async () => {
    try {
      setLoading(true);
      // Use appropriate service method based on payment type
      const response = isInitialPayment
        ? await paymentsService.getEditInitialPaymentForm(effectiveRentId, effectivePaymentId)
        : await paymentsService.getEditPaymentForm(effectiveRentId, effectivePaymentId);
      
      if (response.success) {
        const { 
          payment: paymentData, 
          rent: rentData, 
          guardian: guardianData,
          property: propertyData,
          room: roomData,
          bankAccounts: accounts 
        } = response.data;
        
        setPayment(paymentData);
        setRent(rentData);
        setGuardian(guardianData);
        setProperty(propertyData);
        setBankAccounts(accounts);
        
        // Load guardian image from S3 (backend now returns presigned URL directly)
        if (guardianData?.imageUrl) {
          // Backend already provides presigned URL in imageUrl field
          setGuardianImageUrl(guardianData.imageUrl);
        } else if (guardianData?.image || guardianData?.user?.image) {
          // Fallback: if backend doesn't provide imageUrl, try to generate it (shouldn't happen normally)
          const imageKey = guardianData.image || guardianData.user?.image;
          try {
            // Try to get URL from backend
            const presignedUrl = await s3Service.getFileUrlFromBackend(imageKey);
            setGuardianImageUrl(presignedUrl);
          } catch (imgErr) {
            console.error("Error loading guardian image:", imgErr);
            setGuardianImageUrl(null);
          }
        }
        
        // Populate form with existing payment data (convert dates to dd/mm/yyyy)
        // Fix: Use !== undefined && !== null to allow 0 values (not empty string)
        setFormData({
          amount: paymentData.amount !== undefined && paymentData.amount !== null ? String(paymentData.amount) : '',
          due_date: formatDateForDisplay(paymentData.due_date) || '',
          status: String(paymentData.status !== undefined && paymentData.status !== null ? paymentData.status : '0'),
          amount_paid: paymentData.amount_paid !== undefined && paymentData.amount_paid !== null ? String(paymentData.amount_paid) : '',
          payment_date: formatDateForDisplay(paymentData.payment_date) || '',
          bank_account_id: paymentData.bank_account_id || '',
          notes: paymentData.notes || ''
        });
      } else {
        toast.error('Failed to load payment data');
        navigate('/payments');
      }
    } catch (error) {
      console.error('Error loading payment edit data:', error);
      toast.error('Error loading payment data');
      navigate('/payments');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for native date input
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return '';
    // If already in YYYY-MM-DD format, return as is
    if (dateString.includes('-') && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Handle DD/MM/YYYY format (Laravel format)
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    return '';
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY (Laravel format)
  const convertFromDateInputFormat = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  // Handle date change from native date picker
  const handleDateChange = (fieldName, e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const formattedDate = convertFromDateInputFormat(dateValue);
      setFormData(prev => ({
        ...prev,
        [fieldName]: formattedDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  // Open date picker
  const openDatePicker = (fieldName, e) => {
    e?.preventDefault();
    let dateInputRef;
    if (fieldName === 'due_date') {
      dateInputRef = dueDateInputRef;
    } else if (fieldName === 'payment_date') {
      dateInputRef = paymentDateInputRef;
    }
    
    if (dateInputRef?.current) {
      setTimeout(() => {
        try {
          if (dateInputRef.current.showPicker) {
            dateInputRef.current.showPicker();
          } else {
            dateInputRef.current.click();
          }
        } catch (error) {
          dateInputRef.current.click();
        }
      }, 0);
    }
  };

  const formatDateForBackend = (dateString) => {
    if (!dateString) return null;
    // If already in yyyy-mm-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    // Convert from dd/mm/yyyy to yyyy-mm-dd
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.amount || !formData.due_date || !formData.bank_account_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      // Backend validation expects dd/mm/yyyy format, so send as is (no conversion)
      // Backend controller will convert to yyyy-mm-dd for database storage
      // This matches PaymentCreate.jsx pattern and passes validation middleware
      const submitData = {
        ...formData,
        rent_id: effectiveRentId, // IMPORTANT: Include rent_id to prevent payment from disappearing
        due_date: formData.due_date || null, // Keep dd/mm/yyyy format for validation
        payment_date: formData.payment_date || null // Keep dd/mm/yyyy format for validation
      };

      console.log('📤 [PAYMENT EDIT] Submitting payment update:', {
        rentId: effectiveRentId,
        paymentId: effectivePaymentId,
        isInitialPayment,
        submitData
      });

      // Use appropriate update method based on payment type
      const response = isInitialPayment
        ? await paymentsService.updateInitialPayment(effectiveRentId, effectivePaymentId, submitData)
        : await paymentsService.updatePayment(effectiveRentId, effectivePaymentId, submitData);

      if (response && response.success) {
        toast.success('Payment updated successfully');
        // Simple redirect
        if (rentId) {
          navigate(`/rents/${rentId}/payments`, { replace: false });
        } else {
          navigate('/payments', { replace: false });
        }
      } else {
        const errorMessage = response?.message || 'Failed to update payment';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('❌ [PAYMENT EDIT] Error updating payment:', error);
      console.error('❌ [PAYMENT EDIT] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Handle validation errors (422 status)
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = validationErrors.map(err => `${err.field}: ${err.message}`).join(', ');
        toast.error(`Validation failed: ${errorMessages}`);
        console.error('❌ [PAYMENT EDIT] Validation errors:', validationErrors);
      } else {
        // Show detailed error message
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            error.message || 
                            'Error updating payment';
        toast.error(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to appropriate page based on route
    if (rentId) {
      navigate(`/rents/${rentId}/payments`);
    } else {
      navigate('/payments');
    }
  };

  // Helper functions (matching RentPayments.jsx pattern)
  const safeRender = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') {
      if (value.hasOwnProperty('name')) return value.name;
      if (value.hasOwnProperty('en_short_name')) return value.en_short_name;
      return JSON.stringify(value);
    }
    return String(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return `£${num.toFixed(2)}`;
  };

  // Get payment type name (matching Laravel config/rents.php payment_type_reverse)
  const getPaymentTypeName = (type) => {
    if (!type && type !== 0) return 'Payment'; // Default for regular payments
    const typeMap = {
      0: 'Key payment',      // Laravel: 0 => 'Key payment'
      1: 'License Fee',       // Laravel: 1 => 'License Fee'
      2: 'Admin Fee',         // Laravel: 2 => 'Admin Fee'
      3: 'Fire Safety Pack'  // Laravel: 3 => 'Fire Safety Pack'
    };
    return typeMap[type] || 'Payment';
  };

  // Current data references (matching RentPayments.jsx pattern)
  const currentGuardian = guardian || rent?.guardian || {};
  const currentProperty = property || rent?.property || {};
  const currentRent = rent || {};

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading payment data...</div>
      </div>
    );
  }

  if (!payment || !rent) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Payment or rent data not found</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Laravel Breadcrumb: Dashboard / Properties / {Property} / Payment List / {Guardian} / Payments / Payment */}
      <div className="breadcrumb-row">
        <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
          <span 
            onClick={() => navigate('/dashboard')} 
            style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
          >
            Dashboard
          </span>
        </span>
        <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
          <span 
            onClick={() => navigate('/properties')} 
            style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
          >
            / Properties
          </span>
        </span>
        {currentProperty.id && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            <span 
              onClick={() => navigate(`/properties/${currentProperty.id}`)} 
              style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
            >
              / {safeRender(currentProperty.name)}
            </span>
          </span>
        )}
        {currentProperty.id && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            <span 
              onClick={() => navigate(`/properties/${currentProperty.id}/payments`)} 
              style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
            >
              / Payment List
            </span>
          </span>
        )}
        {!currentProperty.id && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            / Payment List
          </span>
        )}
        {currentGuardian.id && currentGuardian.fullName && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            <span 
              onClick={() => navigate(`/guardians/${currentGuardian.id}`)} 
              style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
            >
              / {safeRender(currentGuardian.fullName)}
            </span>
          </span>
        )}
        {!currentGuardian.id && currentGuardian.fullName && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            / {safeRender(currentGuardian.fullName)}
          </span>
        )}
        {effectiveRentId && effectiveRentId !== 'unknown' && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            <span 
              onClick={() => navigate(`/rents/${effectiveRentId}/payments`)} 
              style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }}
            >
              / Payments
            </span>
          </span>
        )}
        {(!effectiveRentId || effectiveRentId === 'unknown') && (
          <span className="breadcrumb-main" style={{ fontWeight: 400, fontSize: "14px", color: "black" }}>
            / Payments
          </span>
        )}
        <div style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Payment</span>
        </div>
      </div>

      <div className={styles["main-content"]}>
        {/* Guardian Overview Section - Laravel: @include('guardians._guardian_overview') */}
        <div className={styles["row"]}>
          <div className={styles["col-md-12"]}>
            <div className={styles["panel-title"]}>
              <h3 className={styles["title-standard"]}>Guardian overview</h3>
            </div>
            <div className={styles["panel-group"]}>
              <div className={styles["row"]}>
                <div className={styles["col-sm-6"]}>
                  <div className={styles["guardian-avatar-wrapper"]}>
                    {guardianImageUrl ? (
                      <img className={styles["guardian-avatar"]} src={guardianImageUrl} alt="Guardian" />
                    ) : (
                      <div className={styles["guardian-avatar-placeholder"]}></div>
                    )}
                  </div>
                  <dl className={styles["green-list"]}>
                    <div className={styles["list-row"]}>
                      <dt>Guardian ID:</dt>
                      <dd>{safeRender(currentGuardian.unique_guardian_number) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Name:</dt>
                      <dd>{safeRender(currentGuardian.fullName) || safeRender(currentRent.guardianName) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Phone number:</dt>
                      <dd>{safeRender(currentGuardian.phone_number || currentGuardian.user?.phone_number) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Mobile number:</dt>
                      <dd>{safeRender(currentGuardian.mobile_number || currentGuardian.user?.mobile_number) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Email:</dt>
                      <dd>
                        <a href={`mailto:${currentGuardian.email || currentGuardian.user?.email || ''}`} className={styles["mailto-link"]}>
                          {safeRender(currentGuardian.email || currentGuardian.user?.email) || '-'}
                        </a>
                      </dd>
                    </div>
                    {currentGuardian.hear_about && (
                      <div className={styles["list-row"]}>
                        <dt title="How did you hear about Global Guardians?">Hear about GG:</dt>
                        <dd>{safeRender(currentGuardian.hear_about)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className={styles["col-sm-6"]}>
                  <dl className={styles["green-list"]}>
                    <div className={styles["list-row"]}>
                      <dt>Status:</dt>
                      <dd>
                        <span 
                          className={styles["status-label"]}
                          style={{ backgroundColor: currentGuardian.status?.color || '#28a745' }}
                        >
                          {safeRender(currentGuardian.status?.name || currentGuardian.status) || 'Active'}
                        </span>
                      </dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Gender:</dt>
                      <dd>{safeRender(currentGuardian.gender) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt title="Date of Birth">DoB:</dt>
                      <dd>{formatDate(currentGuardian.date_of_birth) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Nationality:</dt>
                      <dd>{safeRender(currentGuardian.nationality || currentGuardian.country?.nationality) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Religion:</dt>
                      <dd>{safeRender(currentGuardian.religion?.name || currentGuardian.religion) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt title="National Insurance Number">NI:</dt>
                      <dd>{safeRender(currentGuardian.national_insurance_number || currentGuardian.nhs_number) || '-'}</dd>
                    </div>
                    {currentGuardian.next_of_kin && (
                      <div className={styles["list-row"]}>
                        <dt>Next of kin</dt>
                        <dd>{safeRender(currentGuardian.next_of_kin)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Details Section - Laravel: @include('payments._property_and_room_details') */}
        <div className={styles["row"]}>
          <div className={styles["col-xs-12"]}>
            <div className={styles["panel-title"]}>
              <h3 className={styles["title-standard"]}>Details</h3>
            </div>
            <div className={styles["panel-group"]}>
              <div className={styles["row"]}>
                <div className={styles["col-xs-6"]}>
                  <dl className={styles["green-list"]}>
                    <div className={styles["list-row"]}>
                      <dt>Property ID:</dt>
                      <dd>{safeRender(currentProperty.property_id || currentProperty.property_public_id) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Property Name:</dt>
                      <dd>{safeRender(currentProperty.name) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Property Status:</dt>
                      <dd>{safeRender(currentProperty.status_text || currentProperty.statusText) || 'Active'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Property address:</dt>
                      <dd>{safeRender(currentProperty.address_1) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Postcode:</dt>
                      <dd>{safeRender(currentProperty.postcode) || '-'}</dd>
                    </div>
                    {currentProperty.date_taken_on && (
                      <div className={styles["list-row"]}>
                        <dt>Lease dates:</dt>
                        <dd>
                          {formatDate(currentProperty.date_taken_on)}
                          {currentProperty.date_taken_back && ` to ${formatDate(currentProperty.date_taken_back)}`}
                        </dd>
                      </div>
                    )}
                    {currentProperty.date_notice && (
                      <div className={styles["list-row"]}>
                        <dt>Notice date:</dt>
                        <dd>{formatDate(currentProperty.date_notice)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div className={styles["col-xs-6"]}>
                  <dl className={styles["green-list"]}>
                    <div className={styles["list-row"]}>
                      <dt>Client</dt>
                      <dd>{safeRender(currentProperty.client?.name) || '-'}</dd>
                    </div>
                    {currentProperty.client?.bankAccount && (
                      <div className={styles["list-row"]}>
                        <dt>GG Bank Account</dt>
                        <dd>{safeRender(currentProperty.client.bankAccount.name)}</dd>
                      </div>
                    )}
                    <div className={styles["list-row"]}>
                      <dt>Room:</dt>
                      <dd>{safeRender(currentRent.room?.name || rent?.room?.name) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Rate:</dt>
                      <dd>{formatCurrency(currentRent.monthly_payment || rent?.monthly_payment) || '£0.00'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Start:</dt>
                      <dd>{formatDate(currentRent.date_start || rent?.date_start) || formatDate(currentRent.dateStartShow) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>End:</dt>
                      <dd>{formatDate(currentRent.date_end || rent?.date_end) || formatDate(currentRent.dateEndShow) || '-'}</dd>
                    </div>
                    <div className={styles["list-row"]}>
                      <dt>Status:</dt>
                      <dd>
                        {currentRent.status === 0 ? 'In progress' :
                         currentRent.status === 1 ? 'Active' :
                         currentRent.status === 2 ? 'Failed' :
                         currentRent.status === 3 ? 'Ended' :
                         safeRender(currentRent.status_text) || 'Active'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Form Section - Laravel: payments/edit.blade.php */}
        <div className={styles["row"]}>
          <div className={styles["col-md-12"]}>
            <div className={styles["panel-title"]}>
              <h3 className={styles["title-standard"]}>
                {isInitialPayment && payment?.type !== undefined && payment?.type !== null 
                  ? getPaymentTypeName(payment.type) 
                  : 'Payment'}
              </h3>
            </div>
            <div className={styles["panel-group"]}>
              <form onSubmit={handleSubmit} className={styles["form-horizontal"]}>
                {/* Amount - Laravel: Form::openGroup('amount') */}
                <div className={styles["form-group"]}>
                  <label htmlFor="amount" className={`${styles["control-label"]} ${styles["col-sm-5"]} ${styles["label-required"]}`}>
                    Amount
                  </label>
                  <div className={styles["col-sm-2"]}>
                    <input
                      type="text"
                      id="amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      className={styles["form-control"]}
                      required
                    />
                  </div>
                </div>

                {/* Amount Paid - Laravel: Form::openGroup('amount_paid') */}
                <div className={styles["form-group"]}>
                  <label htmlFor="amount_paid" className={`${styles["control-label"]} ${styles["col-sm-5"]} ${styles["label-required"]}`}>
                    Amount Paid
                  </label>
                  <div className={styles["col-sm-2"]}>
                    <input
                      type="text"
                      id="amount_paid"
                      name="amount_paid"
                      value={formData.amount_paid}
                      onChange={handleInputChange}
                      className={styles["form-control"]}
                      required
                    />
                  </div>
                </div>

                {/* Due Date - Laravel: Form::openGroup('due_date') */}
                <div className={styles["form-group"]}>
                  <label htmlFor="due_date" className={`${styles["control-label"]} ${styles["col-sm-5"]} ${styles["label-required"]}`}>
                    Due Date
                  </label>
                  <div className={styles["col-sm-2"]} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      id="due_date"
                      name="due_date"
                      value={formData.due_date}
                      readOnly
                      onClick={(e) => openDatePicker('due_date', e)}
                      className={`${styles["form-control"]} ${styles["js-datepicker"]}`}
                      placeholder="dd/mm/yyyy"
                      required
                      style={{ paddingRight: '35px', cursor: 'pointer' }}
                    />
                    {/* Hidden date input for native date picker */}
                    <input
                      ref={dueDateInputRef}
                      type="date"
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        zIndex: 10,
                        fontSize: '16px',
                        padding: '6px 35px 6px 10px',
                        margin: 0,
                        border: 'none',
                        background: 'transparent',
                        boxSizing: 'border-box'
                      }}
                      value={convertToDateInputFormat(formData.due_date)}
                      onChange={(e) => handleDateChange('due_date', e)}
                    />
                    <span
                      className="glyphicon glyphicon-calendar"
                      onClick={(e) => openDatePicker('due_date', e)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#999',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        zIndex: 11,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                {/* Payment Date - Laravel: Form::openGroup('payment_date') */}
                <div className={styles["form-group"]}>
                  <label htmlFor="payment_date" className={`${styles["control-label"]} ${styles["col-sm-5"]}`}>
                    Payment Date
                  </label>
                  <div className={styles["col-sm-2"]} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      id="payment_date"
                      name="payment_date"
                      value={formData.payment_date}
                      readOnly
                      onClick={(e) => openDatePicker('payment_date', e)}
                      className={`${styles["form-control"]} ${styles["js-datepicker"]}`}
                      placeholder="dd/mm/yyyy"
                      style={{ paddingRight: '35px', cursor: 'pointer' }}
                    />
                    {/* Hidden date input for native date picker */}
                    <input
                      ref={paymentDateInputRef}
                      type="date"
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        zIndex: 10,
                        fontSize: '16px',
                        padding: '6px 35px 6px 10px',
                        margin: 0,
                        border: 'none',
                        background: 'transparent',
                        boxSizing: 'border-box'
                      }}
                      value={convertToDateInputFormat(formData.payment_date)}
                      onChange={(e) => handleDateChange('payment_date', e)}
                    />
                    <span
                      className="glyphicon glyphicon-calendar"
                      onClick={(e) => openDatePicker('payment_date', e)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#999',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        zIndex: 11,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                {/* GG Bank Account - Laravel: Form::openGroup('bank_account_id') */}
                <div className={styles["form-group"]}>
                  <label htmlFor="bank_account_id" className={`${styles["control-label"]} ${styles["col-sm-5"]} ${styles["label-required"]}`}>
                    GG Bank Account
                  </label>
                  <div className={styles["col-sm-4"]}>
                    <select
                      id="bank_account_id"
                      name="bank_account_id"
                      value={formData.bank_account_id}
                      onChange={handleInputChange}
                      className={styles["form-control"]}
                      required
                    >
                      <option value="">Select Bank Account</option>
                      {bankAccounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Status - Laravel: Form::openGroup('status') */}
                <div className={styles["form-group"]}>
                  <label htmlFor="status" className={`${styles["control-label"]} ${styles["col-sm-5"]} ${styles["label-required"]}`}>
                    Status
                  </label>
                  <div className={styles["col-sm-2"]}>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className={styles["form-control"]}
                      required
                    >
                      <option value="">Select Status</option>
                      <option value="0">Not paid</option>
                      <option value="1">Paid</option>
                      <option value="4">Plan</option>
                      <option value="5">Legal</option>
                      <option value="6">Notice to quit</option>
                    </select>
                  </div>
                </div>

                {/* Form Actions - Laravel: edit.blade.php fieldset with Cancel and Save buttons */}
                <div className={styles["form-group"]}>
                  <div className={styles["col-sm-12"]}>
                    <div className={styles["button-group"]}>
                      <button
                        type="button"
                        className={`${styles["btn"]} ${styles["btn-default"]}`}
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className={`${styles["btn"]} ${styles["btn-primary"]}`}
                        disabled={saving}
                      >
                        <i className="glyphicon glyphicon-ok"></i> {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentEdit; 